import Utils from "../utils";
import Network from "../miniviews/network";

import FRAGMENT_SHADER from "./shaders/glraycaster.fs";
import VERTEX_SHADER from "./shaders/glraycaster.vs"
import RenderManager from "./render-manager";

import ensureArray from "../utils/ensure-array";
import AssetLibrary from "../assets/asset-library";
import getExtendedObject, { getExtendedGeometry } from "../utils/extended-intersection";
import SlideManager from "../utils/slide-manager";
import isVisible from "../utils/is-visible";

import Mask from '../labels/live/layout/mask';
import CallEvent from '../utils/event';

const USE_LINEAR = !Utils.queryParams.has("log");
const MAX_DISTANCE = Utils.queryParams.getInt("max_distance") || 64;
const PRECISION = 20;

const DISTANCE_SCALE = (255 / MAX_DISTANCE).toFixed(PRECISION);
const INVERT_COLOR = (1 / 255).toFixed(PRECISION);

const g_wPosition = new THREE.Vector3();
const g_lastMatrix = new THREE.Matrix4();
const g_point = new THREE.Vector3();
const g_local = new THREE.Vector3();

class DynamicDelay {
    constructor(params = {}) {
        this.enabled = params.enabled || false;
        this.min = params.min || 2;
        this.max = params.max || 15;
        this.allowedDelay = params.allowedDelay || (1000 / 60);
    }
}

/**
 * DTO for raycaster parameters.
 */
class GLRaycasterParams {
    /**
     * Constructor.
     * @param {Object} params Raycaster parameters.
     */
    constructor(params = {}) {
        this.debugCanvas = params.debug_canvas || null;
        this.enabled = params.enabled || false;

        this.scale = params.scale || 1;

        if (this.debugCanvas) {
            this.debugCanvas.width = this.width;
            this.debugCanvas.height = this.height;
        }

        this.delay = params.delay || 10;
        this.dynamicDelay = new DynamicDelay(params.dynamicDelay);

        this.buildMeshRows = params.build_mesh_rows || false;
    }
}

class GLRaycasterState {
    constructor() {
        this.lastMatrixWorld = new THREE.Matrix4();
        this.lastProjectionInverse = new THREE.Matrix4();
        this.lastProjectionScreenMatrix = new THREE.Matrix4();
        this.frustum = new THREE.Frustum();
        this.hasObjects = false;

        Object.seal(this);
    }

    fromCamera(camera) {
        this.lastProjectionScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        this.frustum.setFromMatrix(this.lastProjectionScreenMatrix);
        this.lastMatrixWorld.copy(camera.matrixWorld);
        this.lastProjectionInverse.getInverse(camera.projectionMatrix);
        this.hasObjects = false;
    }

    /**
     * 
     * @param {GLRaycasterState} other 
     */
    copy(other) {
        this.lastMatrixWorld.copy(other.lastMatrixWorld)
        this.lastProjectionInverse.copy(other.lastProjectionInverse);
        this.lastProjectionScreenMatrix.copy(other.lastProjectionScreenMatrix);
        this.frustum.copy(other.frustum);
        this.hasObjects = other.hasObjects;
    }
}

function calculateWidthHeight(width, height) {
    const ratio = height / width;
    const maxSize = Q3.browserCapabilities.supports.raycaster.maxResolution;

    width = Math.min(maxSize, width);
    height = Math.floor(width * ratio);

    return { width, height };
}

/**
 * WebGL based raycaster. It is limited to a total of 65535 objects per scene and is less acurate with deep zoom.
 */
class GLRaycaster {
    /**
     * Constructor.
     * @param {GLRaycasterParams} params Raycaster parameters.
     */
    constructor(params) {
        this._params = params;

        const scaleRatio = params.scale * getPixelRatioScale();
        const _width = params.width ? params.width * scaleRatio : 240;
        const _height = params.height ? params.height * scaleRatio : 160;

        delete params.width;
        delete params.height;

        let { width, height } = calculateWidthHeight(_width, _height);
        this._width = width, this._height = height;

        this._target = new THREE.WebGLRenderTarget(this._width, this._height);
        this._pixels = new Uint8Array(this._width * this._height * 4);

        this._enabled = true;

        this._depthMaterials = {
            skinning: {
                morphTargets: { 0: createMaterial(true, true, 0) },
                noMorphTargets: { 0: createMaterial(true, false, 0) }
            },
            noSkinning: {
                morphTargets: { 0: createMaterial(false, true, 0) },
                noMorphTargets: { 0: createMaterial(false, false, 0) }
            }
        };

        function confirmMaterials(materials) {
            Object.values(materials).forEach(obj => {
                if (obj.isShaderMaterial) {
                    if (!("needsUniformsUpdate" in obj))
                        console.warn("THREE.JS version was changed, needsRefresh is not defined. Stuff may break.");

                    obj.isRaycastMaterial = true;
                } else {
                    confirmMaterials(obj);
                }
            });
        }

        confirmMaterials(this._depthMaterials);

        this._goodDelayTicks = 0;

        this._regionTracker = new RegionTracker();

        if (params.debugCanvas) {
            /**
             * @type {CanvasRenderingContext2D}
             */
            this._debugCanvasContext = params.debugCanvas.getContext("2d", { alpha: false });
            /**
             * @type {ImageData}
             */
            this._debugCanvasImageData = this._debugCanvasContext.createImageData(this._width, this._height);
        }

        this._ticks = 0;
        this._enqued = false;
        this._enqueAfterUpdate = null;
        this._longestRows = new LongestRows();
        this._stateActive = new GLRaycasterState();
        this._stateReady = new GLRaycasterState();
        this._revisions = -1;
        this._isDecoding = false;

        class GLRaycasterMask extends Mask {
            constructor(raycaster) {
                super();

                this._raycaster = raycaster;
            }

            get width() { return this._raycaster._width; }
            get height() { return this._raycaster._height; }

            makeGetterAtPos() {
                const pixels = this._raycaster._pixels;

                return (pos) => {
                    const off = pos << 2;
                    const r = pixels[off + 0], g = pixels[off + 1];
                    const regionId = r * 255 + g;

                    if (regionId === 0) {
                        return null;
                    }

                    return regionId;
                };
            }
        }

        this._mask = new GLRaycasterMask(this);

        this.onBufferUpdate = new CallEvent();
    }

    get revision() { return this._revisions; }

    get enabled() { return this._enabled; }
    set enabled(value) { this._enabled = value; }

    dispose() {
        this._target.dispose();

        function disposeMaterials(materials) {
            Object.values(materials).forEach(obj => {
                if (obj.isShaderMaterial) {
                    obj.dispose();
                } else {
                    disposeMaterials(obj);
                }
            });
        }

        disposeMaterials(this._depthMaterials);
    }

    untrack(mesh) {
        const currentRegion = this._regionTracker.getRegion(mesh);

        if (currentRegion) this._regionTracker.deleteRegion(mesh);
    }

    makeTrackable(mesh) {
        const currentRegion = this._regionTracker.getRegion(mesh);

        if (!currentRegion) {
            const group = new THREE.Scene();
            group.overrideMaterial = this._depthMaterials
                .noSkinning
                .noMorphTargets[0];

            mesh.subRegions = group;
            group.matrixAutoUpdate = false;

            if (this.getRegionCount(mesh) === 0)
                this._regionTracker.addRegion(mesh);
        }

        mesh.geometry.isReady = false;
        mesh.geometry.onUploadCallback = () => mesh.geometry.isReady = true;
        mesh.onAfterRender = () => mesh.geometry.isReady = true;

    }

    makeTrackableBlob(object, blob, regionId) {
        blob.regionId = regionId;
        blob.geometry.isReady = false;
        blob.geometry.onUploadCallback = () => blob.geometry.isReady = true;
        blob.onAfterRender = () => blob.geometry.isReady = true;
    }

    get width() { return this._width; }
    get height() { return this._height; }
    addRegion(object) { return this._regionTracker.addRegion(object); }

    /**
     * Resizes the raycaster buffer.
     * @param {Number} width Buffer width.
     * @param {Number} height Buffer height.
     */
    resize(width, height) {
        const scaleRatio = this._params.scale * getPixelRatioScale();
        const scaledWidth = Math.round(width * scaleRatio);
        const scaledHeight = Math.round(height * scaleRatio);

        const { width: w, height: h } = calculateWidthHeight(scaledWidth, scaledHeight);
        if (this._width !== w || this._height !== h) {
            this._width = w, this._height = h;

            this._target.setSize(this._width, this._height);
            this._pixels = new Uint8Array(this._width * this._height * 4);

            if (this._params.debugCanvas) {
                this._params.debugCanvas.width = this._width;
                this._params.debugCanvas.height = this._height;
                this._debugCanvasImageData = this._debugCanvasContext.createImageData(this._width, this._height);
            }
        }
    }

    setScissor(x, y, w, h) {
        // this._target.scissor
        //     .set(x, y, w, h)
        //     .multiplyScalar(this._params.scale * getPixelRatioScale())
        //     .round();
    }

    setViewport(x, y, w, h) {
        // this._target.viewport
        //     .set(x, y, w, h)
        //     .multiplyScalar(this._params.scale * getPixelRatioScale())
        //     .round();
    }

    _renderObject({ object, material: visualMaterial, renderer, camera, gl, maxClippings, unusedMaterials }) {
        if (
            //!object.geometry.isReady ||
            !this._stateActive.frustum.intersectsObject(object) ||
            !isVisible(object) ||
            !visualMaterial.visible && !object.isRaycastOnly ||
            !Network.insideCurrentWorld(object)
        ) return;

        if (!object.geometry.isFinal) {
            const oldCallback = object.onAfterRender;
            object.onAfterRender = function () {
                object.geometry.isFinal = true;
                oldCallback && oldCallback();
                object.onAfterRender = oldCallback;
            }
            return;
        }

        gl = gl || renderer.getContext();

        gl.stencilFunc(gl.ALWAYS, 3, 0xff)
        gl.stencilOp(gl.KEEP, gl.KEEP, gl.REPLACE);
        gl.clear(gl.STENCIL_BUFFER_BIT);

        object.updateMatrix();
        object.updateMatrixWorld();

        (object._lastMatrixWorld = object._lastMatrixWorld || new THREE.Matrix4())
            .copy(object.matrixWorld);

        const dataObject = SlideManager.currentSlide.animation[object.uuid];
        const extendedHighlighting = dataObject && "extendedSelectionEnabled" in dataObject ? dataObject.extendedSelectionEnabled : false;

        const isSkinning = !extendedHighlighting && !!(object.skeleton && visualMaterial.skinning);
        const isMorphing = !extendedHighlighting && visualMaterial.morphTargets;
        const useSkinning = isSkinning ? "skinning" : "noSkinning";
        const useMorphTargets = isMorphing ? "morphTargets" : "noMorphTargets";

        const allClipMaterials = this._depthMaterials[useSkinning][useMorphTargets];
        const clippingPlanes = visualMaterial.clippingPlanes || [];
        const clipCount = clippingPlanes.length;

        unusedMaterials[useSkinning][useMorphTargets][clipCount] = false;

        if (object.uuid in maxClippings && !allClipMaterials[clipCount]) {
            allClipMaterials[clipCount] = createMaterial(isSkinning, isMorphing, clipCount);
        }

        const material = allClipMaterials[clipCount] || allClipMaterials[0];
        const regions = this.getRegions(object);

        material.uniforms.worldObjectId.value = regions[0].id;
        // material.needsUpdate = true;
        material.needsUniformsUpdate = true;
        this._stateActive.hasObjects = true;

        const depthBuffer = renderer.state.buffers.depth;
        if (visualMaterial.depthTest === false) {
            depthBuffer.setLocked(true);
            gl.disable(gl.DEPTH_TEST);
        }

        let extendedObject;

        if (extendedHighlighting) {
            const extendedSize = dataObject && "extendedSelectionSize" in dataObject ? dataObject.extendedSelectionSize : 1;

            extendedObject = getExtendedObject(object, camera, extendedSize);
        }

        material.side = "overridePicker" in object && object.overridePicker.type === "ScalpelPlane" || extendedHighlighting
            ? THREE.DoubleSide
            : visualMaterial.side;
        material.clippingPlanes = visualMaterial.clippingPlanes;

        renderer.renderBufferDirect(camera, null,
            extendedHighlighting ? extendedObject.geometry : object.geometry,
            material,
            extendedHighlighting ? extendedObject : object,
            null
        );

        material.side = THREE.FrontSide;

        const subs = object.subRegions;
        if (subs && false) {
            const _children = subs.children;
            const _filteredChildren = subs.children = subs.children.filter(x => x);

            // gl.disable(gl.DEPTH_TEST);
            gl.stencilFunc(gl.EQUAL, 3, 0xff);
            gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
            // depthBuffer.setLocked(true);

            subs.matrix.copy(object.matrix);
            subs.matrixWorld.copy(object.matrixWorld);
            renderer.render(subs, camera, this._target);

            subs.children = _filteredChildren;

            subs.traverseVisible(x => {
                if (!x.isMesh || x.noRaycast) return;

                const region = regions[x.regionId];

                if (!region) return;

                this._depthMaterials.noSkinning.noMorphTargets[0].uniforms.worldObjectId.value = region.id;
                this._depthMaterials.noSkinning.noMorphTargets[0].needsUniformsUpdate = true;

                renderer.renderBufferDirect(camera, null,
                    x.geometry,
                    this._depthMaterials.noSkinning.noMorphTargets[0],
                    x, null);
            });

            subs.children = _children;
        }

        material.clippingPlanes = null;

        gl.enable(gl.DEPTH_TEST);
        depthBuffer.setLocked(false);
    }

    get isReady() { return this._ticks++ >= this._params.delay && this._enqued; }

    updateBuffers(renderer) {

        if (this._ticks++ < this._params.delay || !this._enqued) return;

        if (!this._enabled || !this._stateActive.hasObjects) {
            this._enqued = false;
            this._enqueAfterUpdate = null;
            this._ticks = 0;
            this._revisions = -1;
            return;
        }

        if (!this._isDecoding) {
            this._isDecoding = true;

            requestAnimationFrame(() => {
                this._stateReady.copy(this._stateActive);

                const gl = renderer.getContext();
                const prevTarget = renderer.getRenderTarget();
                renderer.setRenderTarget(this._target);

                if (this._params.dynamicDelay.enabled) {
                    const dynamicDelay = this._params.dynamicDelay;
                    const beginTime = new Date();

                    gl.readPixels(0, 0, this._width, this._height, gl.RGBA, gl.UNSIGNED_BYTE, this._pixels);

                    const dt = new Date().getTime() - beginTime.getTime();

                    if (dt > dynamicDelay.allowedDelay) {
                        const rand = Math.random() * 3;
                        const sign = rand <= 2 ? -1 : 1;
                        this._params.delay = THREE.Math.clamp(this._params.delay + sign, dynamicDelay.min, dynamicDelay.max);
                    } else {
                        if (++this._goodDelayTicks > 5) {
                            const dynamicDelay = this._params.dynamicDelay;
                            this._params.delay = THREE.Math.clamp(this._params.delay - 1, dynamicDelay.min, dynamicDelay.max);
                        }
                    }

                } else {
                    gl.readPixels(0, 0, this._width, this._height, gl.RGBA, gl.UNSIGNED_BYTE, this._pixels);
                }

                if (this._debugCanvasContext) {
                    const pixels = this._pixels.slice();

                    for (let i = 0, len = pixels.length; i < len; i += 4) {
                        const isEmpty = pixels[i + 3] === 0;
                        pixels[i + 0] = isEmpty ? 255 : pixels[i + 0];
                        pixels[i + 1] = isEmpty ? 255 : pixels[i + 1];
                        pixels[i + 2] = isEmpty ? 255 : pixels[i + 2];
                        pixels[i + 3] = 255;
                    }

                    const ctx = this._debugCanvasContext;

                    this._debugCanvasImageData.data.set(pixels);
                    ctx.putImageData(this._debugCanvasImageData, 0, 0);

                }

                renderer.setRenderTarget(prevTarget);

                this._enqued = false;
                this._ticks = 0;

                if (this._params.buildMeshRows) this._buildMeshRows();

                this._revisions++;
                this._isDecoding = false;

                if (this._enqueAfterUpdate) {
                    const [renderer, camera, force] = this._enqueAfterUpdate;
                    this._enqueAfterUpdate = null;
                    this.enqueFrame(renderer, camera, force);
                }

                this.onBufferUpdate.fire();
            });
        }
    }

    _renderFrame({ renderer, camera, clear, objects, ignoreDepth }) {
        const gl = renderer.getContext();

        const prevAlpha = renderer.getClearAlpha();
        const prevAutoClear = renderer.autoClear;
        renderer.autoClear = false;
        renderer.setClearAlpha(0);

        const prevTarget = renderer.getRenderTarget();
        renderer.setRenderTarget(this._target);

        if (clear) renderer.clear();

        gl.enable(gl.STENCIL_TEST);

        const depthBuffer = renderer.state.buffers.depth;

        if (ignoreDepth) {
            depthBuffer.setLocked(true);
            gl.disable(gl.DEPTH_TEST);
        }

        function findObject(uuid) { return AssetLibrary.objects[uuid]; }

        const scalpedObjects = [], maxClippings = {};
        const unusedMaterials = Object
            .keys(this._depthMaterials)
            .reduce((accum, keySkinning) => {
                accum[keySkinning] = Object
                    .keys(this._depthMaterials[keySkinning])
                    .reduce((accum, keyMorphing) => {
                        accum[keyMorphing] = Object
                            .keys(this._depthMaterials[keySkinning][keyMorphing])
                            .reduce((accum, keyPlanes) => {
                                if (keyPlanes > 0)
                                    accum[keyPlanes] = true;

                                return accum;
                            }, {});

                        return accum;
                    }, {});

                return accum;
            }, {});

        Object.keys(Q3.data.assets.objects).forEach(uuidStencil => {
            const data = Q3.data.assets.objects[uuidStencil];

            if (!data || data.type !== "ScalpelPlane") return;

            const scalpel = findObject(uuidStencil);

            if (!scalpel || !scalpel.visible || !isVisible(scalpel)) return;

            const scalped = data.objects
                .map(uuid => findObject(uuid))
                .filter(object => object && objects.find(other => other.uuid === object.uuid) && isVisible(object));

            const localPlane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0)
                .applyMatrix4(scalpel.matrixWorld);

            scalped.forEach(object => {
                const materials = object.material instanceof Array ? object.material : [object.material];

                materials.forEach(material => {
                    material.clippingPlanes.push(localPlane);
                    material.clipping = true;
                });

                scalpedObjects.push(object);

                maxClippings[object.uuid] = maxClippings[object.uuid] || 0;
                maxClippings[object.uuid]++;
            });
        });

        const sorted = objects.reduce((accum, object) => {
            const _object = "overridePicker" in object ? object.overridePicker : object;
            let visible = object.visible, parent = object;

            while ((parent && parent.parent && parent !== RenderManager.activeScene && Network.insideCurrentWorld(parent)) && visible) {
                parent = parent.parent;
                visible = visible && parent.visible;
            }

            if (!visible || !Q3.data.assets.objects[_object.uuid]) return accum;

            const materials = ensureArray(object.material)
            const material = materials[0];
            const container = materials
                .some(material => material.transparent)
                && Q3.data.assets.objects[_object.uuid].ignoreDepth === true
                ? accum.transparent
                : accum.opaque;

            container.push({ object, material });

            return accum;
        }, { transparent: [], opaque: [] });

        sorted.transparent.sort((a, b) => a.object.renderOrder - b.object.renderOrder);
        sorted.opaque.sort((a, b) => a.object.renderOrder - b.object.renderOrder);

        sorted.transparent.forEach(({ object, material }) => {
            this._renderObject({ renderer, camera, object, material, maxClippings, unusedMaterials });
        });

        gl.clear(gl.DEPTH_BUFFER_BIT);

        sorted.opaque.forEach(({ object, material }) => {
            this._renderObject({ renderer, camera, object, material, maxClippings, unusedMaterials });
        });

        if (ignoreDepth) {
            depthBuffer.setLocked(false);
            gl.enable(gl.DEPTH_TEST);
        }

        gl.disable(gl.STENCIL_TEST);

        renderer.setRenderTarget(prevTarget);
        renderer.autoClear = prevAutoClear;

        renderer.setClearAlpha(prevAlpha);

        scalpedObjects.forEach(object => {
            const materials = object.material instanceof Array ? object.material : [object.material];

            materials.forEach(material => {
                while (material.clippingPlanes.length > 0)
                    material.clippingPlanes.pop();

                material.clipping = false;
            });
        });

        Object
            .keys(unusedMaterials)
            .forEach(keySkinning => {
                Object
                    .keys(unusedMaterials[keySkinning])
                    .forEach(keyMorphing => {
                        Object
                            .keys(unusedMaterials[keySkinning][keyMorphing])
                            .forEach(keyPlanes => {
                                if (!unusedMaterials[keySkinning][keyMorphing][keyPlanes])
                                    return;

                                this._depthMaterials[keySkinning][keyMorphing][keyPlanes].dispose();
                                delete this._depthMaterials[keySkinning][keyMorphing][keyPlanes];
                            });
                    });
            });
    }

    clear() {
        this._longestRows.clear();
        this._pixels.fill(0);
    }

    /**
     * Renders a single raycaster tick.
     * @param {THREE.WebGLRenderer} renderer Renderer to use.
     * @param {THREE.Camera} camera Camera used for rendering.
     */
    enqueFrame(renderer, camera, force) {
        if (!!this._params.enabled || !this._enabled) return;

        if (this._enqued && !force) {
            this._enqueAfterUpdate = [renderer, camera, force];
            return;
        }

        this._stateActive.fromCamera(camera);

        const extendedGeometry = getExtendedGeometry();

        if (!extendedGeometry.isReady) {
            renderer.initializeGeometry(extendedGeometry);
            extendedGeometry.isReady = true;
        }

        // RenderManager.groundPlane.visible = false;
        this._renderFrame({
            renderer,
            camera,
            clear: true,
            objects: Object.keys(this._regionTracker._objects).reduce((acc, key) => {
                acc.push(this._regionTracker._objects[key]._object);
                return acc;
            }, [])
        });
        // RenderManager.groundPlane.visible = true;

        this._ticks = 0;
        this._enqued = true;
    }

    /**
     * @returns {THREE.Matrix4} Last world position.
     */
    get lastMatrixWorld() { return this._stateReady.lastMatrixWorld.clone(); }

    /**
     * Builds mesh row data (used for VB1 labels).
     * 
     * TODO: This way of calculating best pixel while fast is flawed. Currently it finds only the longest row,
     *       which for the most part gives relatively good results. However, this solution is prone
     *       to selecting top row for most objects making the labels appear at the top.
     *       Another case where labels do not appear in proper location is when the mesh is slightly
     *       cut off by the viewport. 
     * 
     *       Solution that should have relatively good performance is to create a graph where pixels 
     *       are graph vertices. First generate vertices for each row, where distance from last pixel
     *       for that mesh id to other pixel is delta from the last pixel containing same ID. Do not
     *       create edges that are longer than distance of one. Afterwards, connect each collumn vertex.
     *       After this is done prune vertices into separate branches where the distances between nodes
     *       are greater then one. This way each individual segment in the image will have it's own
     *       graph. After having this graph, finding the actual best global maximum solution.
     * 
     *       Currently there's no reason reason to implement it, since it's reusing VB1 code, 
     *       it just looks weird for me personally.
     */
    _buildMeshRows() {
        const vp = this._target.viewport;

        // const x = Math.floor((1 + vec.x) * (vp.z / 2)) + vp.x;
        // const y = Math.floor((1 + vec.y) * (vp.w / 2)) + vp.y;

        const w = this._width, h = this._height;
        const longestRowsLen = {}, longestRowsX = {}, longestRowsY = {}, occupancy = {};
        const lowestRow = {}, highestRow = {};

        const getId = this._getRegionId.bind(this, this._pixels);
        const getDepth = this._getDepth.bind(this, this._pixels);
        const getOffset = this._toOffset.bind(this, w, h);

        for (let y = 0, x = 0, offset; y < h; y++) {
            offset = getOffset(x, y);
            let curId = getId(offset);
            let curLen = 1;

            for (x = 0; x <= w; x++) {
                offset = getOffset(x, y);
                const id = getId(offset);

                if (id > 0) {
                    const depth = getDepth(offset);

                    occupancy[id] = occupancy[id] === undefined ? 1 : occupancy[id] + 1;
                    lowestRow[id] = lowestRow[id] === undefined ? depth : Math.min(lowestRow[id], depth);
                    highestRow[id] = lowestRow[id] === undefined ? depth : Math.max(lowestRow[id], depth);
                }

                if (curId === id) {
                    curLen++;
                } else {
                    if (curId > 0 && curLen >= (longestRowsLen[curId] || 0)) {
                        const mid = curLen / 2;

                        longestRowsX[curId] = (2 * ((x - vp.x) - mid)) / vp.z - 1;
                        longestRowsY[curId] = (2 * (y - vp.y)) / vp.w - 1;

                        longestRowsLen[curId] = curLen;
                    }

                    curId = id;
                    curLen = 1;
                }
            }
        }

        if (this._debugCanvasContext) {
            const ctx = this._debugCanvasContext;
            Object.keys(longestRowsLen).forEach(id => {
                const x = Math.floor((1 + longestRowsX[id]) * (vp.z / 2)) + vp.x;
                const y = Math.floor((1 + longestRowsY[id]) * (vp.w / 2)) + vp.y;

                //  5 => 360 211 | x: 390, y: 209
                //  6 => 292 201 | x: 291, y: 206
                //  7 => 256 149 | x: 257, y: 145
                //  8 => 293 182 | x: 302, y: 176
                //  9 => 360 191 | x: 355, y: 144
                // 10 => 320 107 | x: 308, y: 105

                ctx.beginPath();
                ctx.strokeStyle = "yellow";
                ctx.fillStyle = "white";
                ctx.arc(x, y, 2, 0, 2 * Math.PI);
                ctx.fill();
                ctx.stroke();
            });
        }

        const size = (w + 1) * h;
        Object.keys(occupancy).forEach(key => {
            occupancy[key] = occupancy[key] / size;
        });


        this._longestRows.set({
            len: longestRowsLen,
            x: longestRowsX,
            y: longestRowsY,
            occupancy: occupancy,
            min: lowestRow,
            max: highestRow
        });
    }

    /**
     * Retrieve longest rows.
     * @returns {LongestRows} Longest rows.
     */
    get longestRows() { return this._longestRows; }

    /**
     * 
     * @param {Number} w 
     * @param {Number} h 
     * @param {Number} x 
     * @param {Number} y
     * @returns {Number} 
     */
    _toOffset(w, h, x, y) { return (x + w * y) << 2; }
    /**
     * 
     * @param {Number} w 
     * @param {Number} h 
     * @param {Number} offset 
     * @returns {THREE.Vector2}
     */
    _fromOffset(w, h, offset) {
        offset = offset >> 2;

        const x = offset % w;
        const y = (offset - x) / w;

        return new THREE.Vector2(x, y);
    }
    _fromRegionId(id) {
        const g = id % 255;
        const r = (id - g) / 255;
        return { r: r, g: g };
    }

    /**
     * 
     * @param {Number} red 
     * @param {Number} green
     * @returns {Number} 
     */
    _toRegionId(red, green) { return (red * 255 + green) || 0; }
    /**
     * 
     * @param {Number[]} pixels 
     * @param {Number} offset
     * @returns {Number} 
     */
    _getRegionId(pixels, offset) { return this._toRegionId(pixels[offset], pixels[offset + 1]); }

    _getDepth(pixels, pixelOffset) {
        const b = pixels[pixelOffset + 2], a = pixels[pixelOffset + 3];

        let depth;

        if (!USE_LINEAR) {
            // const logDepthBufFC = 2 / (Math.log(RenderManager.activeCamera.far + 1.0) / Math.LN2);
            const logDepth = ((b << 8) | a) * MAX_DISTANCE * 0.000015318627450980392; // 1/255/256
            depth = Math.pow(2, logDepth * 2);
            // depth = logDepth;
        } else {
            depth = ((b << 8) | a) * MAX_DISTANCE * 0.000015318627450980392; // 1/255/256
        }

        return depth;
    }

    /**
     * Picks object to render.
     * @param {THREE.Vector2} vec Position to raycast from.
     * @param {THREE.Camera} camera Camera to use for raycasting. (If camera not provided, use last camera used for rendering.)
     * @returns {WebGLRaycastInfo} Raytrace result.
     */
    pick(vec, camera, debugColor = "magenta") {
        camera = null;

        const vp = this._target.viewport;

        const x = Math.floor((1 + vec.x) * (vp.z / 2)) + vp.x;
        const y = Math.floor((1 + vec.y) * (vp.w / 2)) + vp.y;

        const pixelOffset = (x + this._width * y) << 2;

        const r = this._pixels[pixelOffset], g = this._pixels[pixelOffset + 1],
            b = this._pixels[pixelOffset + 2], a = this._pixels[pixelOffset + 3];

        const regionId = r * 255 + g;

        if (regionId === 0) {
            // if (this._debugCanvasContext) {
            //     const ctx = this._debugCanvasContext;

            //     ctx.beginPath();
            //     ctx.strokeStyle = debugColor;
            //     ctx.fillStyle = "white";
            //     ctx.arc(x, y, 2, 0, 2 * Math.PI);
            //     ctx.stroke();
            //     if (regionId > 0) ctx.fill();
            // }

            return new WebGLRaycastInfo(0, undefined, undefined, NaN);
        }

        if (this._debugCanvasContext) {
            const ctx = this._debugCanvasContext;

            ctx.beginPath();
            ctx.strokeStyle = "white";
            ctx.fillStyle = debugColor;
            ctx.arc(x, y, 2, 0, 2 * Math.PI);
            ctx.stroke();
            if (regionId > 0) ctx.fill();
        }

        let depth;

        if (!USE_LINEAR) {
            // const logDepthBufFC = 2 / (Math.log(RenderManager.activeCamera.far + 1.0) / Math.LN2);
            const logDepth = ((b << 8) | a) * MAX_DISTANCE * 0.000015318627450980392; // 1/255/256
            depth = Math.pow(2, logDepth * 2);
        } else {
            depth = ((b << 8) | a) * MAX_DISTANCE * 0.000015318627450980392; // 1/255/256
        }

        // console.log("Current object depth:", depth);

        const state = this._stateReady;

        const wPosition = g_wPosition.set(
            vec.x * state.lastProjectionInverse.elements[0],
            vec.y * state.lastProjectionInverse.elements[5],
            -1)
            .multiplyScalar(depth);

        const point = g_point.copy(wPosition)
            .applyMatrix4(camera ? camera.matrixWorld : state.lastMatrixWorld);


        if (!camera) {
            const state = this._stateReady;
            state.lastProjectionScreenMatrix.multiplyMatrices(RenderManager.activeCamera.projectionMatrix, RenderManager.activeCamera.matrixWorldInverse);
            state.frustum.setFromMatrix(state.lastProjectionScreenMatrix);

            if (!state.frustum.containsPoint(point)) {
                return new WebGLRaycastInfo(0, undefined, undefined, NaN);
            }
        }

        const objectId = this._regionTracker.getRegionObject(regionId);

        const occupancy = this._params.buildMeshRows ? this._longestRows.occupancy[regionId] : NaN;

        let local, parent;
        if (objectId > 0) {
            const mesh = this._regionTracker.getRegionMesh(regionId);

            if (!mesh._lastMatrixWorld) return new WebGLRaycastInfo(0, undefined, undefined, NaN);

            // mesh.updateMatrixWorld();

            // const currentMatrix = new THREE.Matrix4().getInverse(mesh.matrixWorld);
            const lastMatrix = g_lastMatrix.getInverse(mesh._lastMatrixWorld);

            // dt.applyMatrix4(currentMatrix).applyMatrix4(lastMatrix);

            local = g_local.copy(point).applyMatrix4(lastMatrix);
            parent = mesh;
        }

        return new WebGLRaycastInfo({ objectId, regionId, point, occupancy, depth, local, parent });
    }

    getMainRegionId(object) {
        const region = this._regionTracker.getRegion(object);

        return region ? (region._regions[0].id || 0) : 0;
    }

    getRegions(object) {
        const region = this._regionTracker.getRegion(object);

        return region ? region._regions.slice() : [];
    }

    getRegionCount(object) {
        const region = this._regionTracker.getRegion(object);

        return region ? region._regions.length : 0;
    }

    getRegionLocal(objectId, regionId) {
        const regions = this._regionTracker._objects[objectId]._regions;

        const region = regions.filter(a => a.id === regionId)[0];

        return region || null;
    }

    getRegionById(regionId) {
        return this._regionTracker._regions.find(x => x.id === regionId) || null;
    }

    clearTracker() {
        while (this._regionTracker._regions.pop()) { }
        Object.keys(this._regionTracker._objects)
            .forEach(key => delete this._regionTracker._objects[key]);
    }

    getMask() {
        return this._mask;
    }
}

function getPixelRatioScale() { return Math.min(RenderManager.pixelRatio, 1); }

/**
 * DTO for raytrace info.
 */
class WebGLRaycastInfo {
    /**
     * Constructor.
     * @param {Number} objectId Object ID, if not inbetween 0 and 65535 then the object is invalid.
     * @param {Number} regionId ID of the raycasting region.
     * @param {THREE.Vector3} point Intersection point.
     */
    constructor({ objectId = 0, regionId = 0, point = null, occupancy = NaN, depth = NaN, local = null, parent = null }) {
        this.objectId = objectId;
        this.regionId = regionId;
        this.point = point;
        this.occupancy = occupancy;
        this.depth = depth;
        this.local = local;
        this.parent = parent;

        Object.freeze(this);
    }
}

/**
 * DTO for longest rows.
 */
class LongestRows {
    /**
     * Constructor.
     */
    constructor() {
        this.len = {};
        this.x = {};
        this.y = {};
        this.occupancy = {};
        this.min = {};
        this.max = {};
    }

    /**
     * Sets values.
     * @param {number[][]} len Longest rows length.
     * @param {number[][]} x Longest x rows.
     * @param {number[][]} y Longest y rows.
     * @param {number[][]} occupancy Percentage of the screen object occupies.
     */
    set({ len = {}, x = {}, y = {}, occupancy = {}, min = {}, max = {} } = {}) {
        this.len = len;
        this.x = x;
        this.y = y;
        this.occupancy = occupancy;
        this.min = min;
        this.max = max;
    }

    clear() {
        this.len = this.x = this.y = this.occupancy = this.min = this.max = {};
    }
}

export default GLRaycaster;
export { GLRaycaster, GLRaycasterParams, WebGLRaycastInfo, LongestRows, MAX_DISTANCE };

class Region {
    /**
     * 
     * @param {THREE.Mesh} object 
     * @param {Number} id 
     * @param {Number} subId
     */
    constructor(object, id, subId) {
        this.object = object;
        this.id = id;
        this.subId = subId;

        Object.freeze(this);
    }
}

class RegionDTO {
    constructor(object) {
        this._object = object;
        this._regions = [];
    }

    get count() { return this._regions.length; }

    /**
     * 
     * @param {Region} region 
     */
    push(region) {
        this._regions.push(region);
    }
}

class RegionTracker {
    constructor() {
        /**
         * @type {Region[]}
         */
        this._regions = [];

        /**
         * @type {RegionDTO[]}
         */
        this._objects = {};
    }

    /**
     * 
     * @param {THREE.Object3D} object 
     * @returns {RegionDTO}
     */
    getRegion(object) { return this._objects[object.id] || null; }

    /**
     * 
     * @param {THREE.Mesh} object 
     */
    addRegion(object) {
        // const id = Math.floor(Math.random() * 65535);
        const id = this._regions.length + 1;

        const objectRegions = this._objects[object.id] = this._objects[object.id] || new RegionDTO(object);

        const region = new Region(object, id, objectRegions.count - 1);

        objectRegions.push(region);
        this._regions.push(region);

        if (this._objects[object.id]._regions.length > 1 && object.subRegions) {
            const group = new THREE.Group();
            group.region = region;

            object.subRegions.add(group);
        }

        object.region = region;

        return objectRegions.count - 1;
    }

    deleteRegion(object) {
        let index = this._regions.findIndex(x => x.object == object);

        if (index >= 0) this._regions.splice(index, 1);

        index = Object.keys(this._objects).find(key => this._objects[key]._object === object);

        if (index >= 0) delete this._objects[index];
    }

    removeRegion(object, index) {
        // this._objects[object.id]._regions.splice(index, 1);
        this._objects[object.id]._regions[index] = null;
        object.subRegions.children[index - 1] = null;
        // object.subRegions.remove(object.subRegions.children[index - 1]);

    }

    getRegionMesh(regionId) {
        const region = this._regions.find(x => x.id === regionId);

        return region ? region.object : null;
    }

    getRegionObject(regionId) {
        const region = this._regions.find(x => x.id === regionId);

        return region ? region.object.id : 0;
    }
}

function createMaterial(skinning, morphTargets, clipCount) {
    return new THREE.ShaderMaterial({
        vertexShader: VERTEX_SHADER,
        fragmentShader: FRAGMENT_SHADER,
        // morphTargets: true,

        skinning,
        morphTargets,
        clipping: clipCount > 0,

        uniforms: {
            worldObjectId: { value: 0 }
        },
        defines: {
            EPSILON: 1e-3,
            USE_LINEAR,

            MAX_DISTANCE: MAX_DISTANCE.toFixed(PRECISION),
            DISTANCE_SCALE,
            INVERT_COLOR,
            NUM_CLIPPING_PLANES: clipCount
        }
    });
}